/**
 * KYC compliance repository — the compliance officer's queue and case review
 * through the guarded Nest API (`/compliance/kyc/*`, docs/specs/kyc-aml-profile.md
 * "Compliance API"). Nest-only, parsed with the shared contract
 * (`@dallali/shared` kyc-compliance schemas). Every case read is audit-logged
 * server-side.
 *
 * Errors are NestRequestError — `code` (kyc-0xx) and the server's `ar` / `en`
 * text travel with it.
 */
import { API_ROUTES } from "@/api/routes";
import { makeNestRequest } from "@/services/nestRequest";
import {
  kycCaseResponseSchema,
  kycComplianceAccessSchema,
  kycQueueSchema,
  type KycCaseResponse,
  type KycComplianceAccess,
  type KycDecisionInput,
  type KycQueueItem,
  type RecordKycScreenings,
} from "@dallali/shared";

const nestRequest = makeNestRequest("kycCompliance");

const parseCase = (body: unknown): KycCaseResponse => kycCaseResponseSchema.parse(body);

export const kycComplianceRepository = {
  async access(): Promise<KycComplianceAccess> {
    return kycComplianceAccessSchema.parse(await nestRequest<unknown>(API_ROUTES.kycCompliance.access()));
  },

  async queue(): Promise<KycQueueItem[]> {
    return kycQueueSchema.parse(await nestRequest<unknown>(API_ROUTES.kycCompliance.queue()));
  },

  async getCase(profileId: string): Promise<KycCaseResponse> {
    return parseCase(await nestRequest<unknown>(API_ROUTES.kycCompliance.case(profileId)));
  },

  /** The manual checklist: one result per party of the current version. */
  async recordScreenings(profileId: string, body: RecordKycScreenings): Promise<KycCaseResponse> {
    return parseCase(
      await nestRequest<unknown>(API_ROUTES.kycCompliance.screenings(profileId), {
        method: "POST",
        body: JSON.stringify(body),
      }),
    );
  },

  async decide(profileId: string, body: KycDecisionInput): Promise<KycCaseResponse> {
    return parseCase(
      await nestRequest<unknown>(API_ROUTES.kycCompliance.decisions(profileId), {
        method: "POST",
        body: JSON.stringify(body),
      }),
    );
  },
};
